import { AfterViewInit, Component, Inject, OnDestroy, ViewChild } from "@angular/core";
import { LinkActionModel } from "@nms-angular-toolkit/nms-link";
import {
    ColumnConfig,
    FilterColumnParameters,
    FilterOperator,
    NmsTableComponent,
    NmsTableValidatorService,
    PaginatedResponse,
    RequestConfig,
    RowsValidateModel,
    SortConfig
} from "@nms-angular-toolkit/nms-table";
import { STATUS_UNKNOWN_COLOR } from "@nms-ng2/app/shared/constants/colors";
import { RefreshIntervalService } from "@nms-ng2/app/shared/services/refresh-interval/refresh-interval.service";
import { TableFilterService } from "@nms-ng2/app/shared/services/table-filter/table-filter-service";
import {
    ANGULARJS_ROOTSCOPE,
    ANGULARJS_TRANSLATE,
    AUTHENTICATION_SERVICE,
    LOADING_PANE_SERVICE,
    NMS_FEATURES,
    NMS_STATES,
    STATE
} from "@nms-ng2/app/shared/services/upgraded-provider/upgraded-providers";
import { Observable, Subscription } from "rxjs";
import {
    CIRCUIT_STATUS,
    CircuitStatus,
    DwdmCircuitInfo,
    DwdmCircuitTranslations
} from "./dwdm-circuit.model";
import { DwdmCircuitTableService } from "./services/circuit-table.service";
import { CircuitService } from "./services/circuit.service";
import { NmsToastrService } from "@nms-ng2/app/shared/components/elements/nms-toastr/nms-toastr.service";
import { TranslationHelperService } from "@nms-ng2/app/shared/services/util/translation-helper.service";

/**
 * Componente de exibição de Circuitos DWDM.
 */
@Component({
    selector: "dwdm-circuit",
    templateUrl: "./dwdm-circuit.component.html",
    styleUrls: ["./dwdm-circuit.component.scss"]
})
export class DwdmCircuitComponent implements AfterViewInit, OnDestroy {
    @ViewChild(NmsTableComponent)
    nmsTableComponent: NmsTableComponent;

    tableId: string = "nms-dwdm-circuits";
    autoUpdateInterval: number;
    nmsTableChangedSubscription: Subscription;

    requestConfig: RequestConfig = {
        serverPagination: {
            baseUrl: "web/topology/circuit/dwdm/search"
        }
    };

    operators: Array<FilterOperator> = [];
    initialSort: SortConfig[] = [];
    columns: ColumnConfig[] = [];
    circuitActions: LinkActionModel[] = [];
    loggedUser: string;
    columnsSettingsLabels = {};

    constructor(private readonly refreshIntervalService: RefreshIntervalService,
            @Inject(ANGULARJS_TRANSLATE) private readonly translate: any, @Inject(STATE) private readonly $state: any,
            @Inject(NMS_STATES) private readonly nmsStates: any, private readonly tableFilterService: TableFilterService,
            @Inject(ANGULARJS_ROOTSCOPE) private readonly rootScope: any,
            private readonly translationHelper: TranslationHelperService,
            @Inject(LOADING_PANE_SERVICE) private readonly paneService: any,
            private readonly nmsTableValidatorService: NmsTableValidatorService, private readonly circuitService: CircuitService,
            private readonly tableService: DwdmCircuitTableService, private readonly toastrService: NmsToastrService) {
        this.loggedUser = this.rootScope.loggedUser;
        this.refreshIntervalService.getRefreshInterval((interval) => {
            this.autoUpdateInterval = interval;
        });

        const allTranslations = this.tableService.getAllTranslations();

        this.operators = [
            { value: "AND", label: allTranslations.filter.and, selected: true },
            { value: "OR", label: allTranslations.filter.or, selected: false }
        ];

        this.initialSort = [{ column: "name", direction: "asc", label: allTranslations.table.name }];

        this.columnsSettingsLabels = allTranslations.columnsSettings;

        this.setColumns(allTranslations.actions, allTranslations.status);
        this.setCircuitActions(allTranslations.actions);
        this.subscribeTableServiceAction();
    }

    isTableLoaded(): boolean {
        return this.nmsTableComponent !== undefined;
    }

    ngAfterViewInit(): void {
        this.retrieveTableCircuits = this.retrieveTableCircuits.bind(this);
        this.subscribeTableChange();
    }

    retrieveTableCircuits(): Observable<Object> {
        return this.nmsTableComponent.getProvider();
    }

    subscribeTableChange(): void {
        this.nmsTableChangedSubscription = this.nmsTableComponent?.contentChangeEvent.subscribe((circuits: DwdmCircuitInfo[]) => {
            Promise.all(circuits).then(() => {
                this.nmsTableComponent.applyLoading.next(false);
            });
        });
    }

    subscribeTableServiceAction():void {
        this.tableService.reloadListEmmiter.subscribe(()=>{
            this.refreshTable();
        })
    }

    processFilterChanges = (filterParameters: FilterColumnParameters) => {
        const requestFilters = this.tableFilterService.processFilterChanges(filterParameters);
        this.nmsTableComponent.filter(requestFilters);
    };

    ngOnDestroy(): void {
        this.nmsTableChangedSubscription?.unsubscribe();
    }

    navigateToAddCircuit(): void {
        this.$state.go(this.nmsStates.dwdmCircuits.add);
    }

    refreshTable(): void {
        this.nmsTableComponent.loadData().subscribe();
    }

    notifyTableUpdateData({ data }: { data: PaginatedResponse }): void {
        this.nmsTableComponent.updateData(data);
    }

    getStatusColor(status: CircuitStatus): string {
        return CIRCUIT_STATUS[status]?.bgColor || STATUS_UNKNOWN_COLOR;
    }

    getStatusTranslation(status: CircuitStatus): string {
        const translationKey = CIRCUIT_STATUS[status]?.translationKey || "circuit.status.unknown";
        return this.translate.instant(translationKey);
    }

    async handleBeforeRemove(removeFromNms: boolean = false) {
        if (this.tableService.verifyUserPermission(removeFromNms) && this.isValidForRemove(removeFromNms)) {
            const circuitsSelecteds: DwdmCircuitInfo[] = this.nmsTableComponent.selection.selected;

            this.nmsTableValidatorService
                .showConfirmOptions(await this.tableService.getMessageForConfirmOptions(removeFromNms, circuitsSelecteds))
                .then(async () => {

                   this.paneService.showLoadingPane("dwdm.circuit.table.removing.item");

                    let successMessage = "";
                    let response = null;
                    if (removeFromNms) {
                        response = await this.circuitService.removeFromNms(circuitsSelecteds).toPromise();

                        const devicesCount = circuitsSelecteds.length;
                        const messageKey =
                            devicesCount > 1
                                ? "dwdm.circuit.table.item.success.plural.removed.from.nms"
                                : "dwdm.circuit.table.item.success.single.removed.from.nms";

                        successMessage = this.translationHelper.translateWithReplacement(messageKey, devicesCount.toString());
                    } else {
                        response = await this.circuitService.removeAndActivate(circuitsSelecteds[0]).toPromise();
                        successMessage = this.translate.instant("dwdm.circuit.table.item.success.removed");
                    }

                    this.paneService.closeLoadingPane();

                    if(response == null) {
                        this.toastrService.success(successMessage);
                        this.refreshTable();
                    }
                });
        }
    }

    private setCircuitActions(actions: DwdmCircuitTranslations["actions"]): void {
        this.circuitActions = [
            {   id: "add-dwdm-circuit", translateKey: actions.add, action: () => this.navigateToAddCircuit() },
            {
                id: "edit-dwdm-circuit",
                translateKey: actions.edit,
                action: () => this.handleBeforeEdit()
            },
            {   id: "remove-dwdm-circuit", translateKey: actions.remove, action: () => this.handleBeforeRemove(false) },
            {
                id: "remove-dwdm-circuit-from-nms",
                translateKey: actions.removeFromNms,
                action: () => this.handleBeforeRemove(true)
            }
        ];
    }

    handleBeforeEdit() {
        if (this.isValidForEdit()) {
            const circuitSelected: DwdmCircuitInfo = this.nmsTableComponent.selection.selected[0];
            const { resourceId } = circuitSelected;
            this.$state.go(this.nmsStates.dwdmCircuits.edit, {circuitId: resourceId});
        }
    }

    /**
     * Verifica se atende as condições para edição de circuito:
     *  - verifica se possui linha selecionada;
     *  - verifica se é permitido selecionar múltiplas linhas.
     * @returns boolean
     */
    private isValidForEdit(): boolean {
        const multipleRows = false;
        const noCircuitSelectedKey = "dwdm.circuit.table.no.item.selected";
        const multipleCircuitsSelectedKey = "dwdm.circuit.table.select.edit.one.item";
        const rowsSelectedModel = this.buildRowsValidationModel(this.translate.instant(noCircuitSelectedKey));
        const multipleRowsModel = this.buildRowsValidationModel(
            this.translate.instant(multipleCircuitsSelectedKey),
            multipleRows
        );

        return (
            this.nmsTableValidatorService.hasRowSelected(rowsSelectedModel) &&
            this.nmsTableValidatorService.allowMultipleRowsSelected(multipleRowsModel)
        );
    }

    private setColumns(actions: DwdmCircuitTranslations["actions"], status: DwdmCircuitTranslations["status"]): void {
        this.columns = this.tableService.getColumnsConfig(actions, status);
    }

    private getRowsSelected(): number {
        return this.nmsTableComponent.selection.selected.length;
    }

    private buildRowsValidationModel(message: string, multipleRows: boolean = false): RowsValidateModel {
        return {
            rowsSelected: this.getRowsSelected(),
            message,
            allowMultipleSelection: multipleRows
        };
    }

    /**
     * Verifica se atende a condição para remover um circuito.
     * Neste caso, verifica se possui ao menos uma linha selecionada.
     */
    private isValidForRemove(removeFromNms: boolean = false): boolean {
        const rowsSelectedModel = this.buildRowsValidationModel(
            this.translate.instant("dwdm.circuit.table.no.item.selected"),
            removeFromNms
        );
        const multipleRowsModel = this.buildRowsValidationModel(
            this.translate.instant("dwdm.circuit.table.select.one.item"),
            removeFromNms
        );

        return (
            this.nmsTableValidatorService.hasRowSelected(rowsSelectedModel) &&
            this.nmsTableValidatorService.allowMultipleRowsSelected(multipleRowsModel)
        );
    }
}
